import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { customSwal } from '../CustomSwal';
import { ACCEPTED_IMAGE_TYPES, isImageFile, uploadConsoleImage } from './consoleMediaApi';

let uploadSequence = 0;

// Markdown editor in the app's dark theme. Given an uploadTarget ({ consoleId } or { consoleItemId }),
// images can be added from the toolbar, by pasting, or by dragging them in; each one is uploaded
// and inserted at the cursor as markdown.
const MarkdownEditor = ({ value, onChange, height = 300, preview = 'edit', uploadTarget }) => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const selectionRef = useRef(null);
  const mountedRef = useRef(true);
  const valueRef = useRef(value || '');
  valueRef.current = value || '';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const canUpload = Boolean(uploadTarget?.consoleId || uploadTarget?.consoleItemId);
  // Side-by-side preview is too cramped on phones
  const resolvedPreview =
    preview === 'live' && !window.matchMedia('(min-width: 768px)').matches ? 'edit' : preview;

  const setText = (next) => {
    valueRef.current = next;
    onChange(next);
  };

  // Drop a placeholder at the cursor right away, then swap in the real image once it's hosted
  const uploadFiles = async (files, selection) => {
    const images = files.filter(isImageFile);
    if (images.length === 0) {
      customSwal.fire({
        icon: 'error',
        title: 'Unsupported file',
        text: 'Please choose a PNG, JPEG, GIF or WebP image.',
      });
      return;
    }

    const pending = images.map((file) => ({
      file,
      placeholder: `![Uploading ${(file.name || 'image').replace(/[[\]]/g, '')}…](#upload-${++uploadSequence})`,
    }));

    const current = valueRef.current;
    const start = selection?.start ?? current.length;
    const end = selection?.end ?? start;
    setText(current.slice(0, start) + pending.map((p) => p.placeholder).join('\n') + current.slice(end));
    setUploadingCount((count) => count + pending.length);

    await Promise.all(pending.map(async ({ file, placeholder }) => {
      let replacement = '';
      try {
        const { url } = await uploadConsoleImage(file, uploadTarget, token);
        const alt = (file.name || 'image').replace(/\.[^.]+$/, '').replace(/[[\]]/g, '');
        replacement = `![${alt}](${url})`;
      } catch (error) {
        customSwal.fire({ icon: 'error', title: 'Image upload failed', text: error.message });
      }

      // The modal may have closed mid-upload; don't write back into a form that's gone
      if (mountedRef.current) {
        setText(valueRef.current.replace(placeholder, replacement));
        setUploadingCount((count) => count - 1);
      }
    }));
  };

  const toolbarCommands = useMemo(() => {
    if (!canUpload) return undefined;

    const uploadImage = {
      name: 'upload-image',
      keyCommand: 'upload-image',
      buttonProps: { 'aria-label': 'Upload image', title: 'Upload image' },
      icon: <FontAwesomeIcon icon={faCloudArrowUp} style={{ width: 12, height: 12 }} />,
      execute: (state) => {
        selectionRef.current = state.selection;
        fileInputRef.current?.click();
      },
    };

    return commands.getCommands().flatMap((command) => (
      command.name === 'image' ? [command, uploadImage] : [command]
    ));
  }, [canUpload]);

  const textareaProps = canUpload ? {
    onPaste: (e) => {
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.some(isImageFile)) return;
      e.preventDefault();
      uploadFiles(files, { start: e.currentTarget.selectionStart, end: e.currentTarget.selectionEnd });
    },
    onDragOver: (e) => {
      if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault();
    },
    onDrop: (e) => {
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.some(isImageFile)) return;
      e.preventDefault();
      const caret = e.currentTarget.selectionStart;
      uploadFiles(files, { start: caret, end: caret });
    },
  } : undefined;

  return (
    <div data-color-mode="dark">
      <MDEditor
        value={value || ''}
        onChange={(next) => setText(next || '')}
        height={height}
        preview={resolvedPreview}
        commands={toolbarCommands}
        textareaProps={textareaProps}
      />
      {canUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              if (files.length) uploadFiles(files, selectionRef.current);
            }}
          />
          <p className="mt-1 text-xs text-gray-400 text-left">
            {uploadingCount > 0
              ? `Uploading ${uploadingCount} image${uploadingCount === 1 ? '' : 's'}…`
              : 'Tip: paste or drag an image into the editor, or use the upload button in the toolbar.'}
          </p>
        </>
      )}
    </div>
  );
};

export default MarkdownEditor;
